/**
 * useHomeButtons — React Query hook for `ContentReaderPort.listHomeButtons`.
 *
 * Wraps the active `ContentReaderPort` (resolved via {@link useContent}) in a
 * `useQuery` call. Drives the Home button rows (`HomeButtons.tsx`) which render
 * the published `sidebar_link` home buttons in their assigned tab (US15 /
 * FR-060). The sibling of {@link useCuratedCards}; same cache + loading
 * semantics, the per-method `'buttons'` discriminator keeping its cache slot
 * distinct from the curated strip.
 *
 * queryKey design
 * ===============
 *
 * Shape: `['content', 'buttons', limit, locale]` — the `'content'` domain
 * namespace shared by the content hooks, the `'buttons'` per-method
 * discriminator, then `limit` and `locale` so two callers asking for different
 * caps/locales never alias each other (the `useCuratedCards` rationale).
 *
 * Authoritative spec references
 * =============================
 *
 *   - `specs/024-cms-proto-parity/tasks.md` Phase 14 (T082 — member render).
 *   - `apps/web/src/content/ContentReaderPort.ts` (the port being wrapped).
 */

import { type UseQueryResult, useQuery } from '@tanstack/react-query';

import type { HomeButton } from '../ContentReaderPort.js';
import { CONTENT_STALE_TIME } from './queryConfig.js';
import { useContent } from './useContent.js';

/**
 * Options for {@link useHomeButtons}.
 *
 * `limit` is a soft cap on the small Home button row; absent means "all".
 * `locale` is forwarded verbatim to the port (the hook imposes no default of
 * its own so the cache key reflects exactly what the caller passed).
 */
export interface UseHomeButtonsOptions {
  // `?: T | undefined` under `exactOptionalPropertyTypes`: callers pass a
  // state-held bag where `limit` / `locale` may be `undefined`; the hook
  // forwards `{ limit, locale }` straight to the port. Present-undefined is
  // semantically identical to absent here.
  readonly limit?: number | undefined;
  readonly locale?: string | undefined;
}

/**
 * Fetch the published home buttons ordered by `order` ascending.
 *
 * Returns the standard React Query result shape; consumers read `data` (a
 * `readonly HomeButton[]` once resolved, `undefined` while pending),
 * `isPending`, `isError`, etc. `HomeButtons.tsx` renders nothing while pending
 * or on error and nothing for an empty set (P7 — the button section is absent,
 * not an empty shell), and the authored buttons for the active tab once `data`
 * resolves.
 */
export function useHomeButtons(
  options: UseHomeButtonsOptions = {},
): UseQueryResult<readonly HomeButton[], Error> {
  const { limit, locale } = options;
  const port = useContent();

  return useQuery<readonly HomeButton[], Error>({
    queryKey: ['content', 'buttons', limit, locale],
    queryFn: () => port.listHomeButtons({ limit, locale }),
    staleTime: CONTENT_STALE_TIME,
  });
}
